import { memo, useState } from "react";
import LoadingButton, { type LoadingButtonProps } from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import { TriggerableDialog } from "ui/molecules/Dialog";
import useAddMerchantPaymentMethod from "./useAddMerchantPaymentMethod";
import AddMerchantPaymentMethodFilters from "./AddMerchantPaymentMethodFilters";
import Alert from "@mui/material/Alert";

export type AddMerchantPaymentMethodWithFiltersProps = LoadingButtonProps & {
    mode?: 'single' | 'bulk';
    triggerLabel?: string;
    submitLabel?: string;
    dialogTitle?: string;
};

const AddMerchantPaymentMethodWithFilters = ({
    mode = 'single',
    triggerLabel = 'Add Merchant Method',
    submitLabel = 'Add Method',
    dialogTitle = 'Add Merchant Method',
    ...buttonProps
}: AddMerchantPaymentMethodWithFiltersProps) => {
    const [isProcessing, setProcessing] = useState(false);
    const [error, setError] = useState('');

    const { addMerchantPaymentMethod, getFiltersState, onReset } = useAddMerchantPaymentMethod(mode);

    return (
        <TriggerableDialog
            TriggerComponent={({ onClick }) => (
                <LoadingButton
                    variant='contained'
                    color='primary'
                    {...buttonProps}
                    onClick={onClick}
                    loading={isProcessing}
                >
                    {triggerLabel}
                </LoadingButton>
            )}
            DialogActionsComponent={({ onClose }) => (
                <>
                    <Button
                    sx={{
                        width: '180px'
                    }}
                        onClick={()=> {
                            onReset()
                            onClose()
                        }}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        variant='contained'
                        color='primary'
                        loading={isProcessing}
                        disabled={getFiltersState().isSomeValueEmpty}
                        onClick={() => {
                            setProcessing(true);

                            setError('');
                            addMerchantPaymentMethod()
                                .then(() => onClose())
                                .catch(reason => setError(reason instanceof Error ? reason.message : 'Unable to assign payment methods.'))
                                .finally(() => {
                                    onReset()
                                    setProcessing(false)});
                        }}
                    >
                        {submitLabel}
                    </LoadingButton>
                </>
            )}
            ModalProps={{
                DialogProps: {
                    sx: {
                        '& .MuiDialog-container .MuiPaper-root': {
                            width: 'auto',
                            '& .MuiDialogContent-root': {
                                p: 0,
                                border: 'none'
                            }
                        }
                    }
                },
                titleSlot: dialogTitle
            }}
        >
            {error && <Alert severity='error' sx={{ m: 2 }}>{error}</Alert>}
            <AddMerchantPaymentMethodFilters mode={mode} />
        </TriggerableDialog>
    );
};

export default memo(AddMerchantPaymentMethodWithFilters);
